import { safeParseJSON } from "@/utils/File-parsers/safeParseJSON";
import { CustomAttribute } from "@/types";
import type { CustomField } from "../../hooks/useCustomFields";

interface WithCustomAttributes {
  custom_attributes?: CustomAttribute[];
}

export interface ApiCustomAttribute {
  field_id: number;
  field_name: string;
  item_id: number;
  value: string | number | string[] | null;
}

const CUSTOM_ATTRIBUTE_PREFIX = "custom_attribute_";

const parseCustomAttributeFieldId = (key: string): number | null => {
  if (!key.startsWith(CUSTOM_ATTRIBUTE_PREFIX)) return null;
  const fieldId = Number(key.slice(CUSTOM_ATTRIBUTE_PREFIX.length));
  return Number.isInteger(fieldId) ? fieldId : null;
};

const normalizeCustomAttributeValue = (value: unknown): unknown => {
  if (Array.isArray(value)) return value;
  if (typeof value === "string") return safeParseJSON(value);
  return value;
};

export const processEditCustomAttributesForAPI = (
  formData: Record<string, any>,
  customFields: CustomField[],
  itemId: number,
  existingCustomAttributes: CustomAttribute[] = []
): ApiCustomAttribute[] => {
  const customAttributes: ApiCustomAttribute[] = [];

  for (const key in formData) {
    const field_id = parseCustomAttributeFieldId(key);
    if (field_id === null) continue;

    const currentAttr = existingCustomAttributes?.find(
      (attr) => attr.field_id === field_id
    );

    let valuesAreEqual = false;
    if (currentAttr) {
      const currentValue = normalizeCustomAttributeValue(currentAttr.value);
      const newValue = normalizeCustomAttributeValue(formData[key]);
      if (Array.isArray(currentValue) && Array.isArray(newValue)) {
        valuesAreEqual = arraysEqual(currentValue, newValue);
      } else {
        valuesAreEqual = currentValue === newValue;
      }
    }

    if (valuesAreEqual) {
      continue;
    }

    const baseAttr = customFields?.find((attr) => attr.field_id === field_id);

    customAttributes.push({
      field_id: field_id,
      field_name:
        baseAttr?.field_name || currentAttr?.field_name || `Field ${field_id}`,
      item_id: itemId,
      value: formData[key] as string | number | string[] | null,
    });
  }

  return customAttributes;
};

export const transformCustomAttributes = (
  data: WithCustomAttributes
): Record<string, any> => {
  if (!data?.custom_attributes) return {};

  const customFieldValues: Record<string, string | number | string[]> = {};
  data.custom_attributes.forEach((attr: CustomAttribute) => {
    if (attr.editor_type === "tagBox") {
      customFieldValues[`custom_attribute_${attr.field_id}`] = Array.isArray(
        attr.value
      )
        ? attr.value
        : safeParseJSON(attr.value || "[]");
    } else {
      customFieldValues[`custom_attribute_${attr.field_id}`] = attr.value;
    }
  });

  return customFieldValues;
};

export const arraysEqual = (arr1: unknown[], arr2: unknown[]): boolean => {
  if (arr1.length !== arr2.length) return false;
  return arr1.every((val, index) => val === arr2[index]);
};

const isEmptyAttributeValue = (v: unknown): boolean =>
  v === null ||
  v === undefined ||
  v === "" ||
  (Array.isArray(v) && v.length === 0);

export const processCustomAttributesForCreation = (
  formData: Record<string, any>
): { field_id: number; value: string | number | string[] | null }[] => {
  const customAttributes: {
    field_id: number;
    value: string | number | string[] | null;
  }[] = [];

  for (const key in formData) {
    const field_id = parseCustomAttributeFieldId(key);
    if (field_id === null) continue;
    if (isEmptyAttributeValue(formData[key])) continue;

    customAttributes.push({
      field_id: field_id,
      value: formData[key] as string | number | string[] | null,
    });
  }

  return customAttributes;
};

export const extractCustomFieldsFromFormData = (
  formData: Record<string, any>
): Record<string, any> => {
  const cleanedFormData = { ...formData };

  for (const key in cleanedFormData) {
    if (parseCustomAttributeFieldId(key) !== null) {
      delete cleanedFormData[key];
    }
  }

  return cleanedFormData;
};
